import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import AlternateProductContext from './AlternateProductContext';
import alternateProductModel from '../../model/setting/alternate-product.mode';
import { ConfirmAlert, showAlert } from '../../components';
import { useLoading } from '../../utils/LoadingUtil';
import {
  getAlternateProducts as fetchAlternateProducts,
  createNewAlternateProduct as createProduct,
  deleteAlternateProduct as deleteProduct,
  updateAlternateProduct as updateProduct,
  getAlternateProductById as alternateProductById,
} from '../../services/alternateProductService';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/lib/handleApiError';

const AlternateProductContextProvider = ({ children }) => {
  const { alternateProduct, setAlternateProduct, initialState } =
    alternateProductModel();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [alternateProducts, setAlternateProducts] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [alternateProductsList, setAlternateProductsList] = useState([]);
  const [alternateProductsById, setAlternateProductsById] = useState([]);

  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const nav = useNavigate();

  const handleInitCustomQuery = useCallback(() => {
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(10);
    setSortBy('');
    setSortDir('desc');
  }, []);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    handleInitCustomQuery();
  }, [nav]);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return; // skip first automatic fetch
    }
    getAlternateProducts();
  }, [currentPage]);

  const handleError = (error) => {
    try {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        'An error occurred';
      console.log(error?.response?.status, errorMessage);
      handleApiError('error', errorMessage, error?.response?.status); // Use showAlert for error messages
    } catch (e) {
      console.error('Error handling error:', e);
    }
  };

  const getAlternateProducts = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy,
      sortDirection = sortDir,
    ) => {
      startLoading();
      try {
        const data = await fetchAlternateProducts(
          page,
          size,
          search,
          sortField,
          sortDirection,
        );
        setAlternateProductsList(
          data?._embedded?.productMasterAlternateProductDTOList || data,
        );
        setTotalElements(data?.page?.totalElements || 0);
      } catch (error) {
        handleError(error);
        setTotalElements(0);
      } finally {
        stopLoading();
      }
    },
    [currentPage, pageSize, searchQuery, sortBy, sortDir],
  );

  const createNewAlternateProduct = useCallback(
    async (alternateProduct, closeModal) => {
      if (!alternateProduct?.mainProduct?.id) {
        showAlert('error', 'Select Main Product');
        return;
      }
      if (!alternateProduct.alternateProducts.length > 0) {
        showAlert('error', 'Select At least 1 Alternate Product');
        return;
      }

      startLoading();
      try {
        await createProduct(alternateProduct);
        showAlert('success', 'Alternate Product Created Successfully');
        closeModal();
        clearFormData();
        handleInitCustomQuery();
        getAlternateProducts();
        getAlternateProductById(alternateProduct?.mainProduct?.id);
      } catch (error) {
        handleError(error);
      } finally {
        stopLoading();
      }
    },
    [getAlternateProducts],
  );

  const deleteAlternateProduct = useCallback(
    async (id) => {
      const result = await ConfirmAlert({
        confirmBtnText: 'Delete Alternate Product',
      });
      if (!result.isConfirmed) {
        showAlert('error', 'Alternate Product deletion cancelled');
        return;
      }

      startLoading();
      try {
        await deleteProduct(id);
        getAlternateProducts();
        showAlert('success', 'Alternate Product Deleted Successfully');
      } catch (error) {
        handleError(error);
      } finally {
        stopLoading();
      }
    },
    [getAlternateProducts],
  );

  const updateAlternateProduct = useCallback(
    async (alternateProduct, closeModal) => {
      if (!alternateProduct?.mainProduct?.id) {
        showAlert('error', 'Select Main Product');
        return;
      }
      if (!alternateProduct.alternateProducts.length > 0) {
        showAlert('error', 'Select At least 1 Alternate Product');
        return;
      }

      startLoading();
      try {
        await updateProduct(alternateProduct);
        showAlert('success', 'Alternate Product Updated Successfully');
        closeModal();
        getAlternateProducts();
        getAlternateProductById(alternateProduct?.mainProduct?.id);
      } catch (error) {
        handleError(error);
      } finally {
        stopLoading();
      }
    },
    [getAlternateProducts],
  );

  const getAlternateProductById = useCallback(async (id, alternateNo) => {
    startLoading();
    try {
      const data = await alternateProductById(id);
      setAlternateProducts(data.alternateProducts);
    } catch (error) {
      setAlternateProducts([]);
      alternateNo();
    } finally {
      stopLoading();
    }
  }, []);

  const clearFormData = () => {
    setAlternateProduct(initialState);
  };

  const contextValue = useMemo(
    () => ({
      setAlternateProduct,
      initialState,
      createNewAlternateProduct,
      getAlternateProducts,
      deleteAlternateProduct,
      updateAlternateProduct,
      clearFormData,
      isLoading,
      totalElements,
      currentPage,
      pageSize,
      searchQuery,
      alternateProducts,
      setCurrentPage,
      setPageSize,
      setSearchQuery,
      alternateProduct,
      getAlternateProductById,
      alternateProductsList,
      alternateProductsById,
      sortBy,
      setSortBy,
      sortDir,
      setSortDir,
    }),
    [
      setAlternateProduct,
      initialState,
      createNewAlternateProduct,
      getAlternateProducts,
      deleteAlternateProduct,
      updateAlternateProduct,
      clearFormData,
      isLoading,
      totalElements,
      currentPage,
      pageSize,
      searchQuery,
      alternateProducts,
      setCurrentPage,
      setPageSize,
      setSearchQuery,
      alternateProduct,
      getAlternateProductById,
      alternateProductsList,
      alternateProductsById,
      sortBy,
      setSortBy,
      sortDir,
      setSortDir,
    ],
  );

  return (
    <AlternateProductContext.Provider value={contextValue}>
      {children}
    </AlternateProductContext.Provider>
  );
};

export default AlternateProductContextProvider;
export const useAlternateProduct = () => useContext(AlternateProductContext);
